import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { SortFilter } from '../../../components/filters/SortFilter';
import { inSet } from '../../../lib/typeGuards';
import { OfferRequestSortKey } from '../types';

const ALLOWED_SORT_KEYS = new Set<OfferRequestSortKey>(Object.values(OfferRequestSortKey));

export type OfferRequestsToolbarProps = {
    count?: number;
    sort: OfferRequestSortKey;
    onSortChange: (sort: OfferRequestSortKey) => void;
    // "Showing X-Y of total"
    range?: { from: number; to: number };
};

export function OfferRequestsToolbar({ count, sort, onSortChange, range }: OfferRequestsToolbarProps): ReactElement {
    const { t } = useTranslation();

    const sortOptions = [
        { label: t('OFFER_REQUESTS.SORT.NEWEST'), value: OfferRequestSortKey.Newest },
        { label: t('OFFER_REQUESTS.SORT.OLDEST'), value: OfferRequestSortKey.Oldest },
    ];

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'stretch', md: 'flex-end' },
                justifyContent: 'space-between',
                gap: { xs: 1, md: 2 },
                width: '100%',
            }}>
            {count === undefined ? (
                <Skeleton variant="text" width={180} height={40} aria-hidden />
            ) : (
                <Stack spacing={0}>
                    <Typography variant="body2" color="text.secondary">
                        {t('OFFER_REQUESTS.TOOLBAR.FOUND', { count })}
                    </Typography>
                    {range !== undefined && count > 0 && (
                        <Typography variant="caption" color="text.disabled">
                            {t('OFFER_REQUESTS.TOOLBAR.SHOWING', { from: range.from, to: range.to, total: count })}
                        </Typography>
                    )}
                </Stack>
            )}
            <Box sx={{ width: { xs: '100%', md: 220 }, flexShrink: 0 }}>
                <SortFilter
                    fullWidth
                    label={t('OFFER_REQUESTS.SORT.LABEL')}
                    options={sortOptions}
                    value={sort}
                    onChange={(value) => {
                        if (inSet(ALLOWED_SORT_KEYS)(value)) {
                            onSortChange(value);
                        }
                    }}
                />
            </Box>
        </Box>
    );
}
